// src/features/blog/pages/blog-show-page.tsx
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { parseLang, RTL_LANGS, type Lang } from "@majourneys/shared";
import { Container } from "../../../shared/ui/container";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { Carousel, CarouselSlide } from "../../../shared/ui/carousel";
import { Lightbox } from "../../../shared/ui/lightbox";
import { useBlog } from "../hooks/use-blog";
import { useRecentPosts } from "../hooks/use-recent-posts";
import { ArticleBodyWithCards } from "../components/article-body-with-cards";
import { ContentsFold, ContentsPanel, ReadingProgress, useActiveSection } from "../components/article-reading";
import { contentsEntries, withHeadingIds } from "../lib/article-headings";
import { blogReadMinutes } from "../../../shared/ui/blog-card";
import { RelatedPosts } from "../components/related-posts";
import { buildRelatedPosts } from "../lib/build-related-posts";
import { BreadcrumbLd, siteUrl } from "../../../shared/seo/breadcrumb-ld";
import { PageSeo, SITE_ORIGIN } from "../../../shared/seo/page-seo";
import { panelStyle } from "../../../shared/ui/card-frame";

import { Icon } from "@majourneys/icons";
// Fallback locale set for DTOs that predate `availableLocales` (en/fr/ar, the
// historical blog locales). Current DTOs always populate `availableLocales`.
const BLOG_FALLBACK_LOCALES: Lang[] = ["en", "fr", "ar"];

function GlassBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    >
      <div className="absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-[#E5B265]/30 blur-[120px]" />
      <div className="absolute right-[-10%] top-[20%] h-[520px] w-[520px] rounded-full bg-[#110E39]/20 blur-[140px]" />
      <div className="absolute bottom-[-15%] left-[30%] h-[520px] w-[520px] rounded-full bg-[#16a34a]/20 blur-[140px]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(250,249,253,0.5)_0%,rgba(243,241,250,0.2)_50%,rgba(250,249,253,0.6)_100%)]" />
      <svg
        className="absolute inset-0 h-full w-full opacity-[0.015]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <filter id="noise-article">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" />
        </filter>
        <rect width="100%" height="100%" filter="url(#noise-article)" />
      </svg>
    </div>
  );
}

function estimateReadTime(content: string | undefined): number {
  if (!content) return 5;
  const words = content.replace(/<[^>]*>/g, "").split(/\s+/).filter(Boolean).length;
  return Math.max(2, Math.ceil(words / 220));
}

/**
 * Internal venue links in a blog body (canonical 4-segment activity/explore
 * URLs). Deduped by href, order preserved, feeds the ItemList JSON-LD.
 */
function extractVenueLinks(content: string | undefined): Array<{ href: string; name: string }> {
  if (!content) return [];
  const re =
    /<a href="(\/[a-z]{2}\/[a-z0-9-]+\/(?:restaurants|locations|spas|stays|guides|golf|[a-z0-9-]+)\/[a-z0-9-]+)"[^>]*>([^<]+)<\/a>/g;
  const seen = new Set<string>();
  const out: Array<{ href: string; name: string }> = [];
  for (const m of content.matchAll(re)) {
    const href = m[1];
    const name = m[2].replace(/\s+/g, " ").trim();
    if (!name || seen.has(href)) continue;
    seen.add(href);
    out.push({ href, name });
  }
  return out;
}

/** The house mark, standing in for an author avatar. */
function AuthorMark({ size }: { size: "sm" | "lg" }) {
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-white ${
        size === "sm" ? "h-9 w-9 p-1.5" : "h-12 w-12 p-2.5"
      }`}
      style={{ boxShadow: "0 0 0 1px rgba(17,14,57,0.09)" }}
    >
      <img
        src="/images/majourneys-icon.svg"
        alt=""
        aria-hidden
        className="h-full w-full object-contain"
      />
    </div>
  );
}

/**
 * The byline as one line, for phones. The desktop sidebar's card is four rows
 * tall; on a phone it was the first of four panels standing between the reader
 * and the headline, so it collapses to the three facts that matter.
 */
function CompactByline({
  author,
  date,
  readTime,
}: {
  author: string;
  date: string;
  readTime: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5 border-y border-[rgba(17,14,57,0.09)] py-3 lg:hidden">
      <AuthorMark size="sm" />
      <span className="font-sans text-[12.5px] text-[#5a5d77]">
        <span className="font-bold text-[#110E39]">{author}</span>
        {" · "}
        {date}
        {" · "}
        {t("blog.read_time_minutes", { count: readTime })}
      </span>
    </div>
  );
}

function SharePanel() {
  const { t } = useTranslation();
  return (
    <div className="p-5" style={panelStyle(24)}>
      <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#5a5d77]">
        {t("activity.share")}
      </p>
      <div className="mt-3 grid grid-cols-4 gap-2">
        {[
          { icon: "ri-twitter-x-line", href: "#" },
          { icon: "ri-facebook-fill", href: "#" },
          { icon: "ri-whatsapp-line", href: "#" },
          { icon: "ri-link", href: "#" },
        ].map((s) => (
          <a
            key={s.icon}
            href={s.href}
            aria-label={s.icon}
            className="flex h-10 items-center justify-center rounded-xl border border-white/50 bg-white/50 text-[#110E39] transition hover:bg-white/70"
          >
            <Icon name={s.icon} className={`text-[14px]`} aria-hidden />
          </a>
        ))}
      </div>
    </div>
  );
}

function RecentSidebarWidget({ lang }: { lang: Lang }) {
  const { t } = useTranslation();
  const { data: response } = useRecentPosts();
  const posts = response.data?.slice(0, 4) ?? [];
  if (posts.length === 0) return null;
  return (
    <div className="p-5" style={panelStyle(24)}>
      <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#5a5d77]">
        {t("blog.recent_posts")}
      </p>
      <ul className="mt-3 flex flex-col gap-3">
        {posts.map((p) => (
          <li key={p.id}>
            <Link
              to="/$lang/blog/$slug"
              params={{ lang, slug: p.slug }}
              className="flex gap-3 group"
            >
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-white/50">
                <ImageWithFallback
                  src={p.image}
                  alt={p.title}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-[12.5px] font-bold leading-tight text-[#110E39] line-clamp-2 group-hover:text-[#E5B265]">
                  {p.title}
                </p>
                <span className="mt-0.5 font-sans text-[10.5px] text-[#666]">
                  {p.date}
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BlogShowContent() {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { slug: string; lang?: string };
  const { slug } = params;
  const lang = parseLang(params.lang);
  const { data: response } = useBlog(slug);
  const blog = response.data;

  const { data: recentResponse } = useRecentPosts();
  const related = buildRelatedPosts(recentResponse.data ?? [], blog, 3);

  const available: Lang[] = blog.availableLocales ?? BLOG_FALLBACK_LOCALES;
  const isTranslated = available.includes(lang);
  const robots = isTranslated
    ? "index,follow,max-image-preview:large,max-snippet:-1"
    : "noindex,follow";

  const readTime = blogReadMinutes(blog) ?? estimateReadTime(blog.content);
  const isRtl = RTL_LANGS.includes(lang);
  // Every h2/h3 gets a stable slug id (existing ids kept) in the HTML that SSR
  // renders, so the contents links and shared `#section` URLs resolve before
  // any JS runs.
  const { html: bodyHtml, headings } = useMemo(() => withHeadingIds(blog.content), [blog.content]);
  const contents = useMemo(() => contentsEntries(headings), [headings]);
  const activeSection = useActiveSection(contents.map((h) => h.id));
  const articleRef = useRef<HTMLElement>(null);
  const gallery = blog.galleryImages ?? [];
  const hashtags = blog.hashtags ?? [];

  // Hero gallery: galleryImages already leads with the hero image; fall back to
  // the single hero URL when no gallery is attached.
  const heroImages = gallery.length > 0 ? gallery : blog.image ? [blog.image] : [];
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const breadcrumbItems = [
    { name: t("nav.home"), url: siteUrl(lang) },
    { name: t("nav.blog"), url: siteUrl(lang, "/blog") },
    ...(blog.category?.name && blog.category?.slug
      ? [
          {
            name: blog.category.name,
            url: siteUrl(lang, `/blog?category=${blog.category.slug}`),
          },
        ]
      : []),
    { name: blog.title },
  ];

  const excerpt = (blog.excerpt ?? "").trim();
  const plainExcerpt = excerpt.length > 0
    ? excerpt
    : (blog.content ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 200);
  const seoTitle = `${blog.title} | MaJourneys`;
  const seoDescription = plainExcerpt.length > 160 ? `${plainExcerpt.slice(0, 157)}…` : plainExcerpt;
  const canonicalPath = `/${lang}/blog/${slug}`;
  const articleUrl = `${SITE_ORIGIN}${canonicalPath}`;
  const articleLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
    headline: blog.title,
    description: seoDescription,
    image: blog.image ? [blog.image] : undefined,
    datePublished: blog.publishedAtIso ?? blog.date,
    dateModified: blog.publishedAtIso ?? blog.date,
    // Author is the brand for now (no real bylines populated). Organization
    // type avoids the "Person named MaJourneys" type-mismatch flagged by
    // Article rich result validation. Switch to Person when real bylines ship.
    author: /majourneys/i.test(blog.author)
      ? { "@type": "Organization", "@id": "https://majourneys.com/#organization", name: blog.author, url: "https://majourneys.com" }
      : { "@type": "Person", name: blog.author },
    publisher: {
      "@type": "Organization",
      "@id": "https://majourneys.com/#organization",
      name: "MaJourneys",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_ORIGIN}/images/majourneys-logo-light.svg`,
        width: 512,
        height: 512,
      },
    },
    inLanguage: lang === "fr" ? "fr-MA" : lang === "ar" ? "ar-MA" : "en-US",
    articleSection: blog.category?.name,
    keywords: hashtags.length > 0 ? hashtags.join(", ") : undefined,
  };

  // Listicles that link venues (explore/activity pages) get an ItemList:
  // AI engines (Perplexity, AI Overviews) extract ranked lists from it, and
  // it mirrors the internal-link graph the body already carries.
  const venueLinks = extractVenueLinks(blog.content);
  const itemListLd: Record<string, unknown> | null =
    venueLinks.length >= 3
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          numberOfItems: venueLinks.length,
          itemListElement: venueLinks.map((v, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: v.name,
            url: `${SITE_ORIGIN}${v.href}`,
          })),
        }
      : null;

  return (
    <div className="pb-24 pt-8">
      <PageSeo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={canonicalPath}
        lang={lang}
        ogType="article"
        ogImage={blog.image}
        keywords={hashtags}
        robots={robots}
        hreflangLangs={available}
        jsonLd={itemListLd ? [articleLd, itemListLd] : [articleLd]}
      />
      <BreadcrumbLd items={breadcrumbItems} />
      <Container>
        <Link
          to="/$lang/blog"
          params={{ lang }}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/50 bg-white/50 px-3 py-1.5 font-sans text-[11.5px] font-bold text-[#110E39] backdrop-blur transition hover:bg-white/70"
        >
          <Icon name="ri-arrow-left-line" className="rtl:-scale-x-100" aria-hidden /> {t("blog.back_to_journal")}
        </Link>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
          {/* Sticky meta column, desktop only. On a phone these four panels
              stacked above the article, so the reader met the byline, the
              share row, six contents rows and four recent posts before the
              headline. Their mobile counterparts live in the article below. */}
          <aside className="hidden lg:sticky lg:top-24 lg:block lg:h-fit">
            <div className="flex flex-col gap-4">
              <div className="p-5" style={panelStyle(24)}>
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-white/60 bg-white/70 px-3 py-1 font-sans text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#110E39] backdrop-blur">
                  <Icon name="ri-bookmark-line" className="text-[#E5B265]" aria-hidden />
                  {blog.category.name}
                </span>
                <div className="mt-5 flex items-center gap-3">
                  {/* The house mark, not initials. Every article is bylined to
                      MaJourneys (blog.api.ts sets it), so the circle only ever
                      read "MA", the logo says the same thing and is ours. */}
                  <AuthorMark size="lg" />
                  <div className="flex flex-col">
                    <span className="font-heading text-[14.5px] font-bold text-[#110E39]">
                      {blog.author}
                    </span>
                    <span className="font-sans text-[11.5px] text-[#666]">
                      {t("blog.contributor")}
                    </span>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 border-t border-white/50 pt-4">
                  <div className="flex flex-col">
                    <span className="font-sans text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
                      {t("blog.published")}
                    </span>
                    <span className="mt-1 font-sans text-[12.5px] font-semibold text-[#110E39]">
                      {blog.date}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-sans text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
                      {t("blog.read_time")}
                    </span>
                    <span className="mt-1 font-sans text-[12.5px] font-semibold text-[#110E39]">
                      {t("blog.read_time_minutes", { count: readTime })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contents sit right under the byline so they stay on screen
                  in the sticky column while the article is read. */}
              <ContentsPanel entries={contents} active={activeSection} />

              <SharePanel />

              <Suspense fallback={null}>
                <RecentSidebarWidget lang={lang} />
              </Suspense>
            </div>
          </aside>

          {/* Main column */}
          <article ref={articleRef} className="flex min-w-0 flex-col gap-6">
            <ReadingProgress targetRef={articleRef} isRtl={isRtl} />
            <p className="-mb-3 font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-[#5a5d77]">
              {blog.category?.name ? `${blog.category.name} · ` : ""}
              {t("blog.min_read", { count: readTime })}
            </p>
            <h1 className="font-heading text-[36px] font-bold leading-[1.08] text-[#110E39] md:text-[52px]">
              {blog.title}
            </h1>
            {blog.excerpt && (
              <p className="font-heading text-[17px] font-medium italic leading-relaxed text-[#525466] md:text-[19px]">
                {blog.excerpt}
              </p>
            )}

            <CompactByline author={blog.author} date={blog.date} readTime={readTime} />

            <ContentsFold entries={contents} active={activeSection} />

            {heroImages.length === 1 && (
              <button
                type="button"
                onClick={() => setLightboxIndex(0)}
                aria-label={t("blog.open_image")}
                className="block w-full cursor-zoom-in overflow-hidden rounded-[28px] border border-white/40 shadow-[0_12px_40px_rgba(17,14,57,0.08)]"
              >
                <ImageWithFallback
                  src={heroImages[0]}
                  alt={blog.title}
                  className="h-[380px] w-full object-cover md:h-[460px]"
                />
              </button>
            )}

            {heroImages.length > 1 && (
              <Carousel dots gap={16}>
                {heroImages.map((img, i) => (
                  <CarouselSlide key={`${img}-${i}`}>
                    <button
                      type="button"
                      onClick={() => setLightboxIndex(i)}
                      aria-label={t("blog.open_image")}
                      className="block w-full cursor-zoom-in overflow-hidden rounded-[28px] border border-white/40 shadow-[0_12px_40px_rgba(17,14,57,0.08)]"
                    >
                      <ImageWithFallback
                        src={img}
                        alt={i === 0 ? blog.title : `${blog.title} ${i + 1}`}
                        className="h-[380px] w-full object-cover md:h-[460px]"
                      />
                    </button>
                  </CarouselSlide>
                ))}
              </Carousel>
            )}

            {blog.content && (
              <ArticleBodyWithCards html={bodyHtml} locale={lang} />
            )}

            {hashtags.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2 border-t border-white/50 pt-6">
                {hashtags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-white/50 bg-white/60 px-3 py-1 font-sans text-[12px] font-semibold text-[#110E39] backdrop-blur"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Author card inline */}
            <div className="mt-4 flex items-center gap-4 rounded-2xl border border-white/50 bg-white/50 p-5 backdrop-blur-xl">
              <AuthorMark size="lg" />
              <div className="flex flex-col">
                <span className="font-heading text-[15px] font-bold text-[#110E39]">
                  {t("blog.written_by", { author: blog.author })}
                </span>
                <span className="font-sans text-[12.5px] text-[#666]">
                  {t("blog.author_bio")}
                </span>
              </div>
              <button className="ms-auto rounded-full border border-[#110E39] px-4 py-2 font-sans text-[12px] font-bold text-[#110E39] transition hover:bg-[#110E39] hover:text-white">
                {t("blog.follow")}
              </button>
            </div>

            {/* Phones get share and recent posts here instead, sharing is
                something you do after reading, and recent posts are onward
                reading. On desktop both live in the sticky column. */}
            <div className="flex flex-col gap-4 lg:hidden">
              <SharePanel />
              <Suspense fallback={null}>
                <RecentSidebarWidget lang={lang} />
              </Suspense>
            </div>

            {/* Next / prev strip */}
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <Link
                to="/$lang/blog"
                params={{ lang }}
                className="group rounded-2xl border border-white/50 bg-white/40 p-4 backdrop-blur transition hover:bg-white/60"
              >
                <span className="font-sans text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#5a5d77]">
                  {t("blog.back_to_list")}
                </span>
                <p className="mt-1 font-heading text-[14.5px] font-bold text-[#110E39]">
                  {t("blog.browse_all_entries")}
                </p>
              </Link>
              <Link
                to="/$lang/blog"
                params={{ lang }}
                className="group rounded-2xl border border-white/50 bg-white/40 p-4 text-end backdrop-blur transition hover:bg-white/60"
              >
                <span className="font-sans text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#5a5d77]">
                  {t("blog.more_in_category", { category: blog.category.name })}
                </span>
                <p className="mt-1 font-heading text-[14.5px] font-bold text-[#110E39]">
                  {t("blog.keep_reading")}
                </p>
              </Link>
            </div>
          </article>
        </div>
      </Container>

      {related.length > 0 && (
        <RelatedPosts posts={related} category={blog.category?.name ?? ""} lang={lang} />
      )}

      {lightboxIndex !== null && mounted &&
        createPortal(
          <Lightbox
            images={heroImages}
            activeIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onNavigate={setLightboxIndex}
          />,
          document.body,
        )}
    </div>
  );
}

function BlogShowSkeleton() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#110E39] border-t-transparent" />
    </div>
  );
}

export function BlogShowPage() {
  return (
    <main className="relative min-h-screen">
      <GlassBackdrop />
      <Suspense fallback={<BlogShowSkeleton />}>
        <BlogShowContent />
      </Suspense>
    </main>
  );
}
